/**
 * AppICEMAP — CommentThread Component
 * Linear list of comments with single-level replies.
 *
 * Tag: <comment-thread>
 *
 * Properties:
 *   .comments  — array of comment objects
 *   .loading   — boolean
 *
 * Dispatches:
 *   comment-thread:reply → { commentId: string, authorName: string }
 */

export class CommentThread extends HTMLElement {
	constructor() {
		super();
		this._comments = [];
		this._loading = false;
	}

	connectedCallback() {
		this._render();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get comments() {
		return this._comments;
	}
	set comments(val) {
		this._comments = val || [];
		this._renderContent();
	}

	get loading() {
		return this._loading;
	}
	set loading(val) {
		this._loading = !!val;
		this._renderContent();
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `<div class="comment-thread" role="log" aria-label="Comentarios"></div>`;
		this._threadEl = this.querySelector(".comment-thread");
		this._renderContent();
	}

	_renderContent() {
		if (!this._threadEl) return;

		if (this._loading) {
			this._threadEl.innerHTML = `
        <skeleton-loader type="text" lines="3"></skeleton-loader>
      `;
			return;
		}

		if (!this._comments || this._comments.length === 0) {
			this._threadEl.innerHTML = `
        <div class="comment-thread__empty">
          <i class="ph ph-chat-circle" aria-hidden="true"></i>
          <span>Sé el primero en comentar</span>
        </div>
      `;
			return;
		}

		// Build thread: top-level comments with replies nested
		const topLevel = this._comments.filter((c) => !c.parent);
		const replies = this._comments.filter((c) => c.parent);

		let html = "";
		for (const comment of topLevel) {
			html += this._renderComment(comment, 0);

			// Find replies to this comment
			const commentReplies = replies.filter(
				(r) =>
					r.parent === comment.id || (r.parent && r.parent.id === comment.id),
			);
			for (const reply of commentReplies) {
				html += this._renderComment(reply, 1);
			}
		}

		this._threadEl.innerHTML = html;

		// Bind reply buttons
		this._threadEl.querySelectorAll(".comment__reply-btn").forEach((btn) => {
			btn.addEventListener("click", () => {
				const commentId = btn.dataset.commentId;
				const authorName = btn.dataset.authorName;
				this.dispatchEvent(
					new CustomEvent("comment-thread:reply", {
						detail: { commentId, authorName },
						bubbles: true,
					}),
				);
			});
		});
	}

	_renderComment(comment, depth) {
		const user = comment.user || {};
		const initials = getInitials(user.first_name, user.last_name);
		const displayName = getUserDisplayName(user);
		const timeAgo = relativeTime(comment.created_at);
		const karma = user.karma || 0;

		return `
      <div class="comment${depth > 0 ? " comment--reply" : ""}" id="comment-${comment.id}">
        <div class="comment__avatar" aria-hidden="true">${initials}</div>
        <div class="comment__body">
          <div class="comment__header">
            <span class="comment__author">
              ${escapeHtml(displayName)}
              ${karma > 0 ? `<span class="karma-badge"><i class="ph ph-star" aria-hidden="true"></i>${karma}</span>` : ""}
            </span>
            <span class="comment__time">${timeAgo}</span>
          </div>
          <p class="comment__text">${escapeHtml(comment.body || "")}</p>
          ${
						depth === 0
							? `
          <button class="comment__reply-btn" data-comment-id="${comment.id}"
                  data-author-name="${escapeAttr(displayName)}">
            Responder
          </button>
          `
							: ""
					}
        </div>
      </div>
    `;
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function getInitials(first, last) {
	const f = (first || "").charAt(0).toUpperCase();
	const l = (last || "").charAt(0).toUpperCase();
	return f + l || "?";
}

function getUserDisplayName(user) {
	if (!user) return "Usuario";
	const first = user.first_name || "";
	const last = user.last_name || "";
	return [first, last].filter(Boolean).join(" ") || "Usuario";
}

function relativeTime(dateStr) {
	if (!dateStr) return "";
	const now = Date.now();
	const then = new Date(dateStr).getTime();
	const diff = now - then;
	const seconds = Math.floor(diff / 1000);

	if (seconds < 60) return "ahora mismo";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `hace ${minutes} min`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `hace ${hours}h`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `hace ${days}d`;

	return new Date(dateStr).toLocaleDateString("es-MX", {
		month: "short",
		day: "numeric",
	});
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

function escapeAttr(str) {
	return (str || "").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

customElements.define("comment-thread", CommentThread);
